import { useState } from "react";
import { Upload, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle, X, IndianRupee, Wallet, CreditCard } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import api from "../services/api";
import { toast } from "sonner";

export default function BulkUploadDialog({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = () => {
    const headers = "pickup_address,delivery_address,package_description,weight,item_size,service_type,delivery_speed,scheduled_date,scheduled_time";
    const sample = '"MG Road, Bangalore 560001","Jubilee Hills, Hyderabad 500033","Electronics package",2.5,medium,delivery,express,,';
    const csv = headers + "\n" + sample;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pickngo_bulk_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("payment_method", paymentMethod);
      const { data } = await api.post("/orders/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} orders created!`);
        onSuccess?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setPaymentMethod("cod");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="bulk-upload-dialog" aria-describedby="bulk-upload-desc">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[#FF6B00]" />
            Bulk Upload Orders
          </DialogTitle>
          <p id="bulk-upload-desc" className="text-sm text-slate-500">Upload a CSV file to create multiple orders at once</p>
        </DialogHeader>

        <div className="space-y-4 mt-3">
          {!result ? (
            <>
              {/* Download Template */}
              <button
                onClick={handleDownloadTemplate}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-slate-200 hover:border-[#FF6B00] hover:bg-orange-50/30 transition-all text-left"
                data-testid="bulk-download-template"
              >
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Download className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Download CSV Template</p>
                  <p className="text-[10px] text-slate-400">Get the format with sample data</p>
                </div>
              </button>

              {/* File Upload */}
              <div className="space-y-2">
                <label
                  className={`flex flex-col items-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                    file ? "border-[#FF6B00] bg-orange-50/30" : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="bulk-file-drop"
                >
                  <Upload className={`h-8 w-8 ${file ? "text-[#FF6B00]" : "text-slate-300"}`} />
                  {file ? (
                    <div className="text-center">
                      <p className="text-sm font-semibold text-[#FF6B00]">{file.name}</p>
                      <p className="text-[10px] text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm font-medium text-slate-600">Drop CSV file or click to browse</p>
                      <p className="text-[10px] text-slate-400">Max 500 orders per file</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files[0])}
                    data-testid="bulk-file-input"
                  />
                </label>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Payment Method</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("cod")}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                      paymentMethod === "cod" ? "border-[#FF6B00] bg-orange-50/50" : "border-slate-100"
                    }`}
                    data-testid="bulk-payment-cod"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${paymentMethod === "cod" ? "bg-[#FF6B00]" : "bg-slate-100"}`}>
                      <CreditCard className={`h-4 w-4 ${paymentMethod === "cod" ? "text-white" : "text-slate-400"}`} />
                    </div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${paymentMethod === "cod" ? "text-[#FF6B00]" : "text-slate-700"}`}>COD</p>
                      <p className="text-[10px] text-slate-400">Pay on delivery</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("wallet")}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                      paymentMethod === "wallet" ? "border-emerald-500 bg-emerald-50/50" : "border-slate-100"
                    }`}
                    data-testid="bulk-payment-wallet"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${paymentMethod === "wallet" ? "bg-emerald-600" : "bg-slate-100"}`}>
                      <Wallet className={`h-4 w-4 ${paymentMethod === "wallet" ? "text-white" : "text-slate-400"}`} />
                    </div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${paymentMethod === "wallet" ? "text-emerald-700" : "text-slate-700"}`}>Wallet</p>
                      <p className="text-[10px] text-slate-400">Deduct from balance</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Upload Button */}
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-12 font-semibold btn-press"
                data-testid="bulk-upload-btn"
              >
                {uploading ? (
                  <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Processing orders...</>
                ) : (
                  <><Upload className="h-5 w-5 mr-2" /> Upload & Create Orders</>
                )}
              </Button>
            </>
          ) : (
            /* Result */
            <div className="space-y-4" data-testid="bulk-result">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-emerald-700">{result.created}</p>
                  <p className="text-[10px] text-emerald-600">Orders Created</p>
                </div>
                {result.failed > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <AlertTriangle className="h-6 w-6 text-red-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-red-600">{result.failed}</p>
                    <p className="text-[10px] text-red-500">Failed</p>
                  </div>
                )}
              </div>

              {result.total_amount > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex justify-between items-center">
                  <span className="text-sm text-slate-600">Total Amount</span>
                  <span className="text-lg font-bold text-slate-900 flex items-center gap-0.5">
                    <IndianRupee className="h-4 w-4" />{result.total_amount}
                    <span className="text-[10px] text-slate-400 ml-1">({result.payment_method})</span>
                  </span>
                </div>
              )}

              {result.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 max-h-32 overflow-y-auto">
                  <p className="text-xs font-bold text-red-700 mb-1">Failed Rows:</p>
                  {result.errors.map((e, i) => (
                    <p key={`err-${e.row}`} className="text-[10px] text-red-600">Row {e.row}: {e.error}</p>
                  ))}
                </div>
              )}

              <Button onClick={handleClose} className="w-full rounded-xl h-11 bg-[#FF6B00] text-white hover:bg-[#E65A00] font-semibold" data-testid="bulk-done-btn">
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
